import React, { useState } from 'react';
import { Link2, Check, X } from 'lucide-react';

/**
 * PasteUrlInput — small affordance to add a YouTube/Vimeo/external URL as
 * an attachment. Stays collapsed as a button until tapped, then expands
 * inline. Designed to sit alongside the MediaCaptureMenu without crowding.
 *
 * Props:
 *   onAdd(url) — called when user submits a valid-looking URL
 *   testId
 */
const PasteUrlInput = ({ onAdd, testId = 'paste-url' }) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');

  const submit = () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    // Permissive validation — accept anything that looks URL-shaped.
    // The AttachmentTile renderer handles the visual fallback for
    // non-embeddable links (just shows them as clickable external links).
    if (!/^https?:\/\//i.test(trimmed)) return;
    onAdd(trimmed);
    setUrl('');
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`${testId}-open`}
        className="w-full px-4 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-dashed border-enf-surface-border hover:border-enf-role-info rounded-md text-xs font-medium text-enf-text-secondary hover:text-enf-role-info transition-colors flex items-center justify-center gap-2"
      >
        <Link2 className="w-3.5 h-3.5" /> Or paste a YouTube / Vimeo link
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <input
        type="url"
        autoFocus
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
        placeholder="https://youtu.be/..."
        data-testid={`${testId}-input`}
        className="flex-1 px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
      />
      <button
        type="button"
        onClick={submit}
        disabled={!url.trim()}
        aria-label="Add link"
        data-testid={`${testId}-submit`}
        className="p-2 bg-enf-accent hover:bg-enf-accent-hover disabled:opacity-40 rounded-md text-enf-text-primary"
      >
        <Check className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => { setOpen(false); setUrl(''); }}
        aria-label="Cancel"
        className="p-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-enf-text-secondary"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default PasteUrlInput;
